import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, CalendarPlus, LogOut } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import { Label } from "@/ui/label";
import { Badge } from "@/ui/badge";
import {
  fetchCustomerMe,
  fetchMyBookings,
  fetchMyOrders,
  logoutCustomer,
  publicBookingMessage,
  startCustomerOTP,
  verifyCustomerOTP,
  type CustomerBooking,
  type CustomerPortalOrder,
} from "./api";
import { usePublicBranches } from "./branches";
import { localPhoneFromNormalized } from "./search-params";
import { CustomerOrderCard } from "./CustomerOrderCard";
import { canRequestReschedule, RescheduleRequestForm } from "./RescheduleRequestForm";
import { CustomerChatBubble } from "@/features/customer-chat/CustomerChatBubble";
import { ReferralCard } from "./ReferralCard";
import { publicPageScrollClass } from "@/components/PublicPageScroll";

export function CustomerBookingPortalPage() {
  const queryClient = useQueryClient();
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);

  const meQ = useQuery({
    queryKey: ["customer-auth", "me"],
    queryFn: ({ signal }) => fetchCustomerMe({ signal }),
    retry: false,
  });
  const loggedIn = Boolean(meQ.data);
  const bookingsQ = useQuery({
    queryKey: ["customer-bookings", "me"],
    queryFn: ({ signal }) => fetchMyBookings({ signal }),
    enabled: loggedIn,
  });
  const ordersQ = useQuery({
    queryKey: ["customer-orders", "me"],
    queryFn: ({ signal }) => fetchMyOrders({ signal }),
    enabled: loggedIn,
  });

  const startM = useMutation({
    mutationFn: () => startCustomerOTP({ phone, countryCallingCode: "+84" }),
    onSuccess: async (data) => {
      // skipOtp — BE đang ở chế độ chỉ-SĐT (quyết định 0028): session đã được
      // đặt ngay, vào thẳng danh sách đơn, không có bước nhập mã.
      if (data.skipOtp) {
        toast.success("Đăng nhập thành công");
        await queryClient.invalidateQueries({ queryKey: ["customer-auth"] });
        await queryClient.invalidateQueries({ queryKey: ["customer-bookings"] });
        await queryClient.invalidateQueries({ queryKey: ["customer-orders"] });
        return;
      }
      setOtpSent(true);
      toast.success("Đã gửi mã OTP qua SMS");
    },
    onError: (error) => toast.error(publicBookingMessage(error)),
  });
  const verifyM = useMutation({
    mutationFn: () => verifyCustomerOTP({ phone, countryCallingCode: "+84", code }),
    onSuccess: async () => {
      toast.success("Đăng nhập thành công");
      await queryClient.invalidateQueries({ queryKey: ["customer-auth"] });
      await queryClient.invalidateQueries({ queryKey: ["customer-bookings"] });
      await queryClient.invalidateQueries({ queryKey: ["customer-orders"] });
    },
    onError: (error) => toast.error(publicBookingMessage(error)),
  });
  // Cổng giữ SĐT dạng "84…"; ô nhập của trang đặt lịch cần dạng NỘI ĐỊA —
  // dán thẳng dạng 84 vào là số sai mà trông vẫn như đã điền.
  const localPhone = localPhoneFromNormalized(meQ.data?.phoneNormalized ?? "");

  const logoutM = useMutation({
    mutationFn: logoutCustomer,
    onSuccess: async () => {
      setOtpSent(false);
      setCode("");
      await queryClient.resetQueries({ queryKey: ["customer-auth"] });
      await queryClient.resetQueries({ queryKey: ["customer-bookings"] });
      await queryClient.resetQueries({ queryKey: ["customer-orders"] });
    },
    // Cùng mẫu với hai nút đăng nhập ngay trên. Hỏng mà im lặng là khách vẫn
    // đăng nhập — máy dùng chung ở quầy vẫn mở đơn của khách trước.
    onError: (error) => toast.error(publicBookingMessage(error)),
  });

  const orders = ordersQ.data ?? [];
  const bookings = bookingsQ.data ?? [];
  const shoots = useMemo(() => mergeShoots(orders, bookings), [orders, bookings]);
  const customerName = useMemo(() => firstCustomerName(orders), [orders]);
  const loading = ordersQ.isLoading || bookingsQ.isLoading;
  const loadError = ordersQ.isError ? ordersQ.error : bookingsQ.isError ? bookingsQ.error : null;

  if (!loggedIn) {
    return (
      <main className={`${publicPageScrollClass} booking-monogram-bg px-3 py-5 sm:px-6`}>
        <section className="mx-auto w-full max-w-md rounded-3xl border bg-surface p-5 shadow-sm sm:p-7">
          <Badge variant="yellow">Booking của tôi</Badge>
          <h1 className="mt-4 text-2xl font-semibold">Đăng nhập bằng số điện thoại</h1>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Nhập đúng số điện thoại đã dùng khi đặt lịch/chụp để xem đơn hàng, link ảnh và booking của bạn.
          </p>
          <form
            className="mt-6 space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (otpSent) verifyM.mutate();
              else startM.mutate();
            }}
          >
            <div className="space-y-1.5">
              <Label>Số điện thoại</Label>
              <div className="grid grid-cols-[6rem_minmax(0,1fr)] gap-2">
                <div className="flex min-h-11 items-center justify-center rounded-xl border bg-surface-2 text-sm font-semibold">+84</div>
                <Input value={phone} onChange={(event) => setPhone(event.target.value)} inputMode="tel" placeholder="090..." className="min-h-11" />
              </div>
            </div>
            {otpSent && (
              <div className="space-y-1.5">
                <Label>Mã OTP</Label>
                <Input value={code} onChange={(event) => setCode(event.target.value)} inputMode="numeric" placeholder="6 chữ số" className="min-h-11" />
              </div>
            )}
            <Button className="min-h-12 w-full" disabled={!phone.trim() || startM.isPending || verifyM.isPending || (otpSent && !code.trim())}>
              {otpSent ? "Xác nhận OTP" : "Xem đơn hàng của tôi"}
            </Button>
            {otpSent && (
              <Button type="button" variant="ghost" className="w-full" onClick={() => startM.mutate()} disabled={startM.isPending}>
                Gửi lại mã
              </Button>
            )}
          </form>
          <a href="/booking" className="mt-5 block text-center text-sm font-semibold text-primary">
            Quay lại trang đặt lịch
          </a>
        </section>
      </main>
    );
  }

  return (
    <main className={`${publicPageScrollClass} booking-monogram-bg px-3 py-5 sm:px-6`}>
      <section className="mx-auto w-full max-w-5xl">
        <header className="flex flex-col gap-3 rounded-3xl bg-[var(--color-brand)] p-5 text-white shadow-brand sm:flex-row sm:items-center sm:justify-between sm:p-7">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.22em] text-white/65">Gạo Nâu Profile Studio</div>
            {/* Chào đích danh (CEO chốt 23/08/2026). Khách mới đặt lịch online,
                chưa có đơn nào thì chưa biết tên — quay về tiêu đề cũ. */}
            <h1 className="mt-1 truncate text-2xl font-semibold">
              {customerName ? `Xin chào ${customerName}` : "Booking của tôi"}
            </h1>
            <p className="mt-2 text-sm text-white/95">SĐT: +{meQ.data?.phoneNormalized}</p>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            {/* Khách đang xem đơn cũ là lúc dễ đặt buổi tiếp theo nhất. */}
            <Button asChild className="bg-white text-[var(--color-brand)] hover:bg-white/90">
              <a href={`/booking?sdt=${encodeURIComponent(localPhone)}`}>
                <CalendarPlus className="mr-2 h-4 w-4" /> Đặt chụp tiếp
              </a>
            </Button>
            <Button variant="secondary" onClick={() => logoutM.mutate()} disabled={logoutM.isPending}>
              <LogOut className="mr-2 h-4 w-4" />
              Đăng xuất
            </Button>
          </div>
        </header>

        <h2 className="mt-6 text-lg font-semibold">Đơn hàng &amp; ảnh của bạn</h2>
        <div className="mt-3 grid gap-3">
          {loading ? (
            <div className="rounded-3xl border bg-surface p-5 text-sm text-muted-foreground">Đang tải đơn hàng…</div>
          ) : loadError ? (
            <div className="rounded-3xl border bg-surface p-5 text-sm text-danger-text">
              {publicBookingMessage(loadError)}
              <Button
                className="mt-3"
                variant="outline"
                onClick={() => {
                  void ordersQ.refetch();
                  void bookingsQ.refetch();
                }}
              >
                Tải lại
              </Button>
            </div>
          ) : shoots.length === 0 ? (
            <div className="rounded-3xl border bg-surface p-5 text-sm text-muted-foreground">
              Chưa tìm thấy buổi chụp nào theo số điện thoại này.
            </div>
          ) : (
            shoots.map((shoot) =>
              shoot.order ? (
                <CustomerOrderCard key={shoot.key} order={shoot.order} booking={shoot.booking} />
              ) : (
                <BookingCard key={shoot.key} booking={shoot.booking!} />
              ),
            )
          )}
        </div>

        {/* "Giới thiệu của tôi" (spec §9.3) — tự ẩn khi chưa đủ điều kiện. */}
        <ReferralCard />
      </section>
      <CustomerChatBubble />
    </main>
  );
}

/**
 * Một buổi chụp dưới mắt khách — KHÔNG phải một bản ghi trong máy.
 *
 * Trong máy, cùng một buổi chụp có thể nằm ở hai bảng: `customer_bookings`
 * (khách tự đặt online) và `orders` (đơn studio mở). Trước 23/08/2026 portal
 * bày hai bảng thành hai danh sách, hậu quả:
 *   · khách đặt online thấy buổi chụp của mình HAI LẦN, chữ nghĩa khác nhau;
 *   · khách tới quầy (walk-in) không có bản ghi booking nên đọc dòng "Chưa
 *     tìm thấy booking theo số điện thoại này" — tưởng hệ thống mất dữ liệu.
 * CEO chốt gộp làm một: mỗi buổi chụp đúng một thẻ.
 */
type Shoot = {
  key: string;
  /** Có đơn thì thẻ đơn là thẻ chính (nhiều thông tin hơn: tiền, ảnh, tip). */
  order?: CustomerPortalOrder;
  /** Lịch online tương ứng — có thì thẻ mọc thêm nút "Yêu cầu đổi lịch". */
  booking?: CustomerBooking;
  /** Mốc xếp thứ tự: ngày chụp, không có thì ngày tạo. */
  sortAt: number;
};

/**
 * mergeShoots — nối lịch online vào đơn hàng theo `linkedOrderId`.
 *
 * Lịch chưa thành đơn vẫn đứng riêng một thẻ (khách đặt online, studio chưa
 * mở đơn). Lịch trỏ tới đơn KHÔNG có trong danh sách cũng đứng riêng thay vì
 * biến mất — đơn có thể rơi ngoài tầm nhìn vì hai bên khớp SĐT theo hai luật
 * khác nhau (đơn khớp 9 số cuối, lịch khớp số đã chuẩn hoá).
 */
export function mergeShoots(orders: CustomerPortalOrder[], bookings: CustomerBooking[]): Shoot[] {
  const bookingByOrderId = new Map<string, CustomerBooking>();
  for (const booking of bookings) {
    if (booking.linkedOrderId) bookingByOrderId.set(booking.linkedOrderId, booking);
  }

  // Đơn đã huỷ không phải buổi chụp của khách nữa: không có ảnh, không có gì
  // để trả, và thẻ chỉ còn mỗi cái nhãn "Đã huỷ" nằm giữa các buổi thật.
  const visible = orders.filter((order) => order.status !== "cancelled");

  const shoots: Shoot[] = visible.map((order) => ({
    key: `order:${order.id}`,
    order,
    ...(bookingByOrderId.get(order.id) ? { booking: bookingByOrderId.get(order.id)! } : {}),
    sortAt: timeOf(order.shootDate) ?? timeOf(order.createdAt) ?? 0,
  }));

  // Đơn huỷ kéo theo lịch online của nó sang 'cancelled' NHƯNG vẫn giữ
  // linkedOrderId (ReconcileCancelInTx). Nếu chỉ đối chiếu với danh sách đã
  // lọc thì cái lịch ấy tưởng mình mồ côi và mọc lại thành thẻ riêng — giấu
  // đơn xong lại hiện đúng buổi đó dưới một mã khác. Đối chiếu với TOÀN BỘ
  // đơn, kể cả đơn vừa giấu.
  const orderIds = new Set(orders.map((order) => order.id));
  for (const booking of bookings) {
    if (booking.linkedOrderId && orderIds.has(booking.linkedOrderId)) continue;
    shoots.push({
      key: `booking:${booking.id}`,
      booking,
      sortAt: timeOf(booking.shootDate) ?? timeOf(booking.createdAt) ?? 0,
    });
  }

  return shoots.sort((a, b) => b.sortAt - a.sortAt);
}

function timeOf(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
}

/** Tên khách lấy từ đơn gần nhất có ghi tên — bản ghi lịch online đã bị máy
 *  chủ lọc tên đi (sanitizeCustomerBooking), không lấy ở đó được. */
function firstCustomerName(orders: CustomerPortalOrder[]): string {
  for (const order of orders) {
    const name = (order.customerName ?? "").trim();
    if (name !== "") return name;
  }
  return "";
}

/** Thẻ cho lịch khách tự đặt mà studio CHƯA mở đơn. */
function BookingCard({ booking }: { booking: CustomerBooking }) {
  const { branches: publicBranches } = usePublicBranches();
  const branch = publicBranches.find((item) => item.code === booking.branchCode);

  return (
    <article className="rounded-3xl border bg-surface p-4 shadow-sm sm:p-5">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={booking.status === "converted" || booking.status === "confirmed" ? "green" : "outline"}>
          {bookingStatusLabel(booking.status)}
        </Badge>
        <span className="text-xs text-muted-foreground">#{booking.publicReference}</span>
      </div>
      <h3 className="mt-3 text-lg font-semibold">{booking.serviceName ?? "Dịch vụ chụp"}</h3>
      <div className="mt-2 grid gap-1 text-sm text-muted-foreground">
        <span className="flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-primary" />
          {formatBookingDate(booking.shootDate)} · {booking.startTime}-{booking.endTime}
        </span>
        <span>{booking.branchName || branch?.shortLabel || booking.branchCode}</span>
        <span>Tổng tiền cơ bản: {formatVND(booking.productPrice)}</span>
        <span>Đã thanh toán: {formatVND(booking.paymentPaidAmount)}</span>
      </div>
      {booking.decisionReason && (
        <div className="mt-3 rounded-2xl bg-brand/10 p-3 text-sm text-primary">{booking.decisionReason}</div>
      )}
      {canRequestReschedule(booking.status) && (
        <div className="mt-4 flex flex-wrap items-start gap-2 border-t pt-3">
          <RescheduleRequestForm bookingId={booking.id} />
        </div>
      )}
    </article>
  );
}

function bookingStatusLabel(status: string) {
  switch (status) {
    case "confirmed":
      return "Đã xác nhận";
    case "converted":
      return "Đã dùng / có order";
    case "pending_payment":
      return "Chờ thanh toán";
    case "payment_expired":
      return "Hết hạn thanh toán";
    case "cancelled":
      return "Đã huỷ";
    default:
      return status;
  }
}

function formatBookingDate(value: string) {
  return new Intl.DateTimeFormat("vi-VN", { dateStyle: "medium" }).format(new Date(value));
}

function formatVND(value: number | undefined) {
  return new Intl.NumberFormat("vi-VN", { style: "currency", currency: "VND", maximumFractionDigits: 0 }).format(value ?? 0);
}
